import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TermsConditionSectionProps } from "_interfaces/terms-conditions";
import TermsConditionsContent from "component/terms-conditions/terms-conditions-content";
import TermsSideNav from "component/terms-conditions/terms-conditions-navItem";
import { QueryOptions } from "functions/buildQueryParams";
import { GetTermsConditions } from "functions/http-requests/terms-conditions";
import MainContainer from "parts/mainContainer";
import SectionTitle from "parts/sectionTitle";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const TermsConditions: React.FC = () => {
  const params = useParams();
  const { breakpoints } = useTheme();
  const isSmallDevice = useMediaQuery(breakpoints.down("md"));

  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);

  const [pageData, setPageData] = useState<TermsConditionSectionProps[]>([]);
  const [navListItem, setNavListItem] = useState<
    TermsConditionSectionProps["ListItem"]
  >([]);

  const fetchPageData = () => {
    const query: QueryOptions = {
      populate: { Sections: ["ListItem"] },
      filters: { Slug: { $eq: "terms-and-conditions" } },
    };
    GetTermsConditions(query)
      .then((res) => {
        const DATA = res?.data?.data;
        const listItems = DATA?.flatMap((item: any) =>
          item?.Sections?.filter((section: any) => section?.ListItem).flatMap(
            (section: any) => section?.ListItem
          )
        );
        if (listItems?.length) setNavListItem(listItems);
      })
      .catch(() => {});
  };

  const fetchPageDataBySlug = (slug?: string) => {
    const query: QueryOptions = {
      populate: { Sections: [] },
      filters: { Slug: { $eq: slug || "" } },
    };

    GetTermsConditions(query)
      .then((res) => {
        const DATA = res?.data?.data;
        const PAGE_DATA = DATA?.flatMap((item: any) =>
          item?.Sections?.filter(
            (section: any) => section?.__component !== "layout.link-item-list"
          )
        );
        setPageData(PAGE_DATA);
      })
      .catch(() => {})
      .finally(() => setIsDataLoading(false));
  };

  // FETCH ONLY LIST ITEM
  useEffect(() => {
    fetchPageData();
  }, []);

  // FETCH PAGE DATA
  useEffect(() => {
    fetchPageDataBySlug(params?.slug || "terms-and-conditions");
  }, [params.slug]);

  if (isDataLoading) {
    return (
      <Box py={15} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (!pageData?.length) {
    return (
      <Typography fontSize={30} py={15} textAlign="center" fontWeight={700}>
        No Data Found
      </Typography>
    );
  }

  return (
    <section>
      <MainContainer>
        <Box py={5} className="fm-poppins">
          <SectionTitle
            sx={{ fontSize: { xs: 24, sm: 30, md: 36 } }}
            title="Terms and Conditions of Use for Talkio Mobile Services"
          />
          <Box pt={3}>
            <Grid container spacing={2}>
              {navListItem?.length ? (
                <Grid
                  item
                  md={3}
                  sx={{
                    maxWidth: "-webkit-fill-available",
                    position: isSmallDevice ? "sticky" : "unset",
                    bottom: isSmallDevice ? 0 : "unset",
                    order: {
                      xs: 2,
                      md: 1,
                    },
                  }}
                >
                  <TermsSideNav navListItem={navListItem} />
                </Grid>
              ) : null}

              <Grid
                item
                md={navListItem?.length ? 9 : 12}
                sx={{
                  maxWidth: "-webkit-fill-available",
                  order: { xs: 1, md: 2 },
                }}
              >
                <Box
                  sx={{
                    li: {
                      lineHeight: 1.5,
                      py: 0.8,
                    },
                    ol: {
                      counterReset: "item",
                      li: {
                        display: "block",
                        "&:before": {
                          counterIncrement: "item",
                          content: "counters(item, '.') '. '",
                        },
                      },
                    },
                    "ol[start='2'], ol[start='3']": {
                      counterReset: "item 1",
                    },
                    "ol[start='3']": {
                      counterReset: "item 2",
                    },
                    "ol[start='4']": {
                      counterReset: "item 3",
                    },
                    "ol[start='5']": {
                      counterReset: "item 4",
                    },
                    "ol[start='6']": {
                      counterReset: "item 5",
                    },
                    "ol[start='7']": {
                      counterReset: "item 6",
                    },
                  }}
                >
                  <TermsConditionsContent data={pageData} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </MainContainer>
    </section>
  );
};

export default TermsConditions;
